import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { NS } from '@/constants';
import { isObjectEqual } from '@/utils/comparisonUtils';
import { formatUrl, getHrefForCategLink } from '@/utils/linkUtils';
import { computeProps, getCriticsRatingNotes, getItemList, getProductPageRedirectionInfos } from '@/utils/productCardUtils';
import { CallToActionKind } from '@/types';
import {
  getCurrentProductItem,
  getAttributeValue,
  getAttribute,
  isItemLightPriceUSA,
  getAttributeId
} from '@/utils/productUtils';
import { useAppDispatch, useAppSelector } from '@/redux';
import { addAlert } from '@/redux/slices/primeur.slice';

import WishListIcon from '@/components/account/wishList/WishListIcon';
import CategViewLabelBio from '@/components/categ/CategViewLabelBio';
import ProductTagList from '@/components/common/badge/ProductTagList';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import ProductRatingInProductList from '@/components/common/product/rating/ProductRatingInProductList';
import AvailabilityIndicator from '@/components/common/AvailabilityIndicator';
import ItemSelector from '@/components/common/ItemSelector';
import MillesimaLink from '@/components/common/MillesimaLink';
import ProductCardAttributes from '@/components/listing/ProductCardAttributes';
import ProductCardFormat from '@/components/listing/ProductCardFormat';
import ProductCardImage from '@/components/listing/ProductCardImage';
import ProductCardName from '@/components/listing/ProductCardName';
import ProductPrice from '@/components/product/ProductPrice';

import styles from './ProductCard.module.scss';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { ATTRIBUTES, ENVIRONNEMENT } from '@/constants/product';
import { STORE_ID } from '@/constants/stores';
import { URLS } from '@/constants/urls';
import { VisibilityTrackingContext } from '@/components/categ/ProductListing';

export const ProductCard = ({
  index, activeFilter, searchMode, product, onClickProduct, isMobile, position, isWishList
}) => {
  const { t } = useTranslation(NS.COMMON);
  const dispatch = useAppDispatch();
  const storeConf = useAppSelector(({ root }) => root.storeConf);
  const categ = useAppSelector(({ categ }) => categ.content);
  const primeurUser = useAppSelector(({ primeur }) => primeur.primeurUser);
  const [quantity, setQuantity] = useState(1);
  const currentItemDraft = getCurrentProductItem(product, activeFilter, categ.sort, categ, searchMode, storeConf.storeId);
  const childrenProps = computeProps({ t, currentItem: currentItemDraft, product, categ, storeConf });
  const { brand, brandId, urlBrand, producerCateg, groupIdentifierBrand, isSpiritBrand } = childrenProps;
  const [currentItem, setCurrentItem] = useState(currentItemDraft || {});
  const ratings = getCriticsRatingNotes(product, storeConf);
  const promotion = getAttribute(currentItem, ATTRIBUTES.RIBBONAD);
  const infoSiteLabel = getAttributeValue(currentItem, ATTRIBUTES.INFO_SITE);
  const environnement = getAttribute(product, ATTRIBUTES.ENVIRONNEMENT);
  const isAlert = getAttributeId(product, 'alerte_o_n') === '1';
  const isAlerted = primeurUser?.alerts?.includes(product?.partnumber);
  const isMobileOrTablet = useScreenSizeContext().isMobileOrTablet;
  const isLabelBioDisplayed = Object.values(ENVIRONNEMENT).includes(environnement?.valueId);

  const productCardRef = useRef(null);
  const visibilityTracking = useContext(VisibilityTrackingContext);

  useEffect(() => {
    const element = productCardRef.current;

    if (element && visibilityTracking) {
      element.dataset.productId = product._id;

      visibilityTracking.observeProduct(element);

      return () => {
        visibilityTracking.unobserveProduct(element);
      };
    }
  }, [visibilityTracking]);

  // This is false only if (we are on mobile device) and if (there is no label and we are not on wishlist).
  const hasLabelOrWishListIcon = !(isMobile && !(promotion.valueId || infoSiteLabel || isWishList));
  const productCardClassName = [
    styles.container,
    isWishList ? `${styles['separator-wishlist']} ${styles['no-horizontal-padding']}` : `${styles['border-left']} ${styles.separator}`
  ].join(' ');
  const imgContainer = [
    styles.imgContainer,
    product.mktOperation != null && styles.background
  ].filter(Boolean).join(' ');

  const backgroundImg = product.mktOperation && { backgroundImage: `url(${product.mktOperation.imgBgS})` };

  const onItemChange = (partnumber) => {
    const item = product.items?.find((i) => i.partnumber === partnumber) ||
      product.items[0] || {};
    if (!isObjectEqual(currentItem, item)) {
      setCurrentItem(item);
    }
  };

  useEffect(() => {
    const newCurrentItem = getCurrentProductItem(product, activeFilter, categ.sort, categ, searchMode, storeConf.storeId) || {};
    if (!isObjectEqual(currentItem, newCurrentItem)) {
      setCurrentItem(newCurrentItem);
    }
  }, [product, activeFilter, categ, searchMode]);

  const setBrandLink = () => (
    <MillesimaLink
      id={`product${index}-goToProducer`}
      href={getHrefForCategLink(urlBrand, groupIdentifierBrand, isSpiritBrand)}
      as={formatUrl(urlBrand, isSpiritBrand, storeConf)}
      ariaLabel={brand}
      innerHtml={brand}
      size="small"
    />
  );

  const setAvaibilityIndicator = () => (
    <AvailabilityIndicator
      t={t}
      product={product}
      currentItem={currentItem}
      locale={storeConf.locale}
      storeId={storeConf.storeId}
      customClassName={styles.availability}
    />
  );

  const isOutOfStock = Object.keys(currentItem).length === 0 || currentItem.stock === 0;
  const itemHasTag = promotion?.valueId || infoSiteLabel;

  return (
    <div
      ref={productCardRef}
      className={productCardClassName}
      data-test-id="product-card-container"
      data-rbf="product-card-container"
      sensefuel-id="product-card-container"
    >
      <div className={styles['rating-and-bio']}>
        <ProductRatingInProductList ratings={ratings} ratingMaxNumber={isMobile ? 1 : undefined}/>
        {(!isMobile && isLabelBioDisplayed) && (
          <div className={`ml-2 ${ratings.length > 0 ? 'mt-3' : ''}`}>
            <CategViewLabelBio
              t={t}
              environnement={environnement}
              isMobileView={isMobileOrTablet}
              isSpirit={product.isSpirit}
              storeConf={storeConf}
              productPosition={position}
            />
          </div>
        )}
      </div>
      <div className={imgContainer} style={backgroundImg}>
        <ProductCardImage
          position={position}
          product={product}
          currentItem={currentItem}
          storeConf={storeConf}
        />
      </div>
      <div className={`${styles.infosContainer} no-gutters`}>
        {hasLabelOrWishListIcon &&
          <div className={itemHasTag ? styles['tag-and-wishlist'] : styles.wishlist}>
            {(promotion.valueId || infoSiteLabel) && (
              <ProductTagList
                t={t}
                promotion={promotion}
                infoSiteLabel={infoSiteLabel}
                lightPrice={isItemLightPriceUSA(currentItem, storeConf.storeId)}
              />
            )}
            {(isWishList || !isMobile) && (
              <WishListIcon product={product} withCloseIcon={isWishList}/>
            )}
          </div>
        }
        <div className="col-12 col-md-9 mb-2">
          <ProductCardName
            index={index}
            product={product}
            currentItem={currentItem}
            storeConf={storeConf}
          />
          <ProductCardAttributes customClassName="mb-2" {...childrenProps} index={index}/>
          {isMobile && (
            <>
              {isLabelBioDisplayed && (
                <div className="mt-2 mb-3">
                  <CategViewLabelBio
                    t={t}
                    environnement={environnement}
                    isMobileView={isMobile}
                    isSpirit={product.isSpirit}
                    storeConf={storeConf}
                    productPosition={position}
                  />
                </div>
              )}
              {Object.keys(currentItem).length > 0 && (
                <ProductCardFormat
                  t={t}
                  currentItem={currentItem}
                  productItems={getItemList(product)}
                  onClick={() => onClickProduct(product)}
                />
              )}
            </>
          )}
        </div>

        <div className={`col-12 col-md-3 mb-2 ${styles['product-price']}`}>
          <ProductPrice currentItem={currentItem} product={product} isMobile={isMobile}/>
        </div>

        {!isMobile && (
          <>
            <div className="col-12 py-2">
              <ItemSelector
                t={t}
                product={product}
                currentItem={currentItem}
                onItemChange={onItemChange}
                quantity={quantity}
                setQuantity={setQuantity}
              />
            </div>

            <div className={`col-12 mt-2 ${styles['producer-availability']}`}>
              {brandId && brand && urlBrand && !producerCateg && setBrandLink()}
              {(currentItem.stock || currentItem.availability) && setAvaibilityIndicator()}
            </div>
          </>
        )}

        {isMobile && (
          <>
            {(currentItem.stock || currentItem.availability) && (
              <div className="col-12">
                {setAvaibilityIndicator()}
              </div>
            )}
            <div className={`col-12 ${styles.actionsContainer}`}>
              {isAlert ? (
                <CallToActionContainer
                  onClick={() => dispatch(addAlert({
                    alerts: [product?.partnumber],
                    name: product?.productName || product?.name,
                    isAllChecked: undefined,
                    storeId: storeConf.storeId
                  }))}
                  icon={{
                    url: isAlerted ? URLS.CROSS_BLACK : URLS.NOTIFICATION_WHITE,
                    alt: 'alert'
                  }}
                  kind={isAlerted ? CallToActionKind.SECONDARY : CallToActionKind.PRIMARY}
                >
                  {isAlerted ? t('alert.unsubscribe') : t('alert.alertMe')}
                </CallToActionContainer>
              ) : (
                <CallToActionContainer
                  onClick={() => onClickProduct(product)} sensefuelId="add-to-cart" id="add-to-cart"
                  disabled={isOutOfStock}
                >
                  {isOutOfStock ? (
                    t('product.noStock', { ns: NS.COMMON })
                  ) : (
                    t('productCard.quickView', { ns: NS.CATEG })
                  )}
                </CallToActionContainer>
              )}
              {!isWishList && (
                <WishListIcon product={product} withCloseIcon={isWishList}/>
              )}
            </div>
            {brandId && brand && urlBrand && !producerCateg && (
              <div className="col-12 mt-3">
                {setBrandLink()}
                {storeConf.storeId === STORE_ID.DEUTSCHLAND && (
                  <>
                    &nbsp;|&nbsp;
                    <MillesimaLink
                      ariaLabel={t('product.details.plural')}
                      innerHtml={t('product.details.plural')}
                      size="small"
                      {...getProductPageRedirectionInfos(product, currentItem, storeConf)}
                    />
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

ProductCard.propTypes = {
  index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  activeFilter: PropTypes.string,
  searchMode: PropTypes.bool,
  product: PropTypes.object,
  onClickProduct: PropTypes.func,
  isMobile: PropTypes.bool.isRequired,
  position: PropTypes.number,
  isWishList: PropTypes.bool,
};

export default ProductCard;
